import React from 'react';
import { useTranslation } from 'react-i18next';
import InlineNameEditor from '../ui/InlineNameEditor';
import Tooltip from '../ui/Tooltip';
import { preloadLexicalEditor } from '../ui/LexicalRichEditorLazy';
import { openDocumentTab } from '../../store/tabsStore';
import { rowIndent, guideOffsets, ROW_ICON_SIZE, SUBPAGE_ICON_SIZE, ROW_CHEVRON_SIZE, ROW_ACTION_ICON_SIZE } from './sidebarStyles';
import { PageGlyph, FolderGlyph, FolderOpenGlyph, ChevronGlyph, PlusGlyph, MoreGlyph } from './treeIcons';

/** The right-hand meta of a row: "move here" while it is a drop target, else the draft tag or the child count. */
function RowMeta({ t, isDropInside, isDraft, childCount, isFolder }) {
  if (isDropInside) return <span className="doc-tree-meta doc-tree-meta--drop">{t('docSidebar.moveHere')}</span>;
  if (isDraft) return <span className="doc-tree-meta doc-tree-meta--draft">{t('docSidebar.draft')}</span>;
  if (!childCount) return null;
  return (
    <span className="doc-tree-meta">{isFolder ? childCount : t('docSidebar.subCount', { count: childCount })}</span>
  );
}

/**
 * One tree row (folder or page) and, when it is open, its children —
 * recursive. `ctx` carries the section's state and actions so every level
 * reads the same values (see TreeSection's rowCtx).
 *
 * Per the "Sidebar — row states" artboard (cards 1.5 / 1.6 / 1.8): 30 px, a
 * chevron on folders and on pages with sub-pages (space kept otherwise), the
 * glyph (open folders tinted, sub-pages 14 px), the label, then the child
 * count — swapped on hover / keyboard focus for "add inside" and "more". The
 * active page gets a neutral fill and the 2 px accent bar at the sidebar edge.
 */
export default function TreeRow({ node, depth, parentType = null, ctx }) {
  const { t } = useTranslation();
  const {
    sectionId,
    scope,
    searching,
    visibleIds,
    childrenByParent,
    expandedFolders,
    editingId,
    selectedId,
    activeDocumentTabId,
    focusId,
    drop,
    draggingId,
    rowDragProps,
    setSelected,
    setEditing,
    toggleFolder,
    handleRename,
    addInside,
    openRowMenu,
    onRowKeyDown,
  } = ctx;

  if (searching && visibleIds && !visibleIds.has(node.id)) return null;
  const isFolder = node.type === 'folder';
  const children = childrenByParent.get(node.id) || [];
  // Documents can hold sub-pages, so a page WITH children is expandable like a
  // folder; a childless folder keeps its chevron (a container by declaration).
  const isExpandable = isFolder || children.length > 0;
  const isOpen = isExpandable && (searching || expandedFolders.has(node.id));
  const isEditing = editingId === node.id;
  const isActive = !isFolder && activeDocumentTabId === `document-editor:${node.id}`;
  const isSelected = selectedId === node.id;
  const isDraft = !isFolder && !node.name;
  const dropHere = drop && drop.id === node.id ? drop.position : null;
  const isSubpage = !isFolder && parentType === 'document';

  const classes = ['doc-tree-row'];
  if (isActive) classes.push('doc-tree-row--active');
  else if (isSelected) classes.push('doc-tree-row--selected');
  if (dropHere) classes.push(`doc-tree-row--drop-${dropHere}`);
  if (draggingId === node.id) classes.push('doc-tree-row--lifted');

  const Glyph = isFolder ? (isOpen ? FolderOpenGlyph : FolderGlyph) : PageGlyph;
  const glyphClass = `doc-tree-icon${isFolder && isOpen ? ' doc-tree-icon--open' : ''}`;
  const addLabel = isFolder ? t('docSidebar.newDocumentInside') : t('docSidebar.newSubpage');
  const stop = (e) => e.stopPropagation();

  return (
    <>
      <div
        role="treeitem"
        aria-level={depth + 1}
        aria-selected={isSelected || isActive}
        aria-expanded={isExpandable ? isOpen : undefined}
        aria-current={isActive ? 'page' : undefined}
        tabIndex={focusId === node.id ? 0 : -1}
        data-drag-id={`${node.type}-${node.id}`}
        data-node-id={node.id}
        style={{ paddingLeft: rowIndent(depth) }}
        className={classes.join(' ')}
        {...(isEditing ? {} : rowDragProps(node))}
        onMouseEnter={isFolder ? undefined : preloadLexicalEditor}
        onClick={() => {
          if (isEditing) return;
          setSelected({ sectionId, id: node.id });
          // A folder row only selects; expanding is the chevron's job (owner's
          // call — the row is also the target for rename, delete and export).
          // Pages open on click: opening is what clicking a page means.
          if (!isFolder) openDocumentTab({ documentId: node.id, label: node.name || t('docSidebar.untitled'), scope });
        }}
        onContextMenu={(e) => openRowMenu(e, node)}
        onKeyDown={(e) => onRowKeyDown(e, node)}
      >
        {guideOffsets(depth).map((left) => (
          <span key={left} className="doc-tree-guide" style={{ left }} aria-hidden />
        ))}
        {isExpandable ? (
          <button
            type="button"
            tabIndex={-1}
            className={`doc-tree-chevron${isOpen ? ' doc-tree-chevron--open' : ''}`}
            onPointerDown={stop}
            onClick={(e) => {
              e.stopPropagation();
              setSelected({ sectionId, id: node.id });
              toggleFolder(node.id);
            }}
            aria-label={isOpen ? t('docSidebar.collapse') : t('docSidebar.expand')}
          >
            <ChevronGlyph size={ROW_CHEVRON_SIZE} />
          </button>
        ) : (
          <span className="doc-tree-spacer" aria-hidden />
        )}

        <Glyph size={isSubpage ? SUBPAGE_ICON_SIZE : ROW_ICON_SIZE} className={glyphClass} />

        {isEditing ? (
          <InlineNameEditor value={node.name} onSave={(name) => handleRename(node, name)} onCancel={() => setEditing(null)} />
        ) : (
          <span className={`doc-tree-label${node.name ? '' : ' doc-tree-label--empty'}`}>
            {node.name || (isFolder ? t('docSidebar.untitledFolder') : t('docSidebar.untitled'))}
          </span>
        )}

        {!isEditing && (
          <>
            <RowMeta t={t} isDropInside={dropHere === 'inside'} isDraft={isDraft} childCount={children.length} isFolder={isFolder} />
            <span className="doc-tree-actions">
              <Tooltip content={addLabel} side="bottom">
                <button
                  type="button"
                  className="doc-tree-action"
                  aria-label={addLabel}
                  onPointerDown={stop}
                  onClick={(e) => {
                    e.stopPropagation();
                    addInside(node);
                  }}
                >
                  <PlusGlyph size={ROW_ACTION_ICON_SIZE} />
                </button>
              </Tooltip>
              <Tooltip content={t('docSidebar.moreActions')} side="bottom">
                <button
                  type="button"
                  className="doc-tree-action"
                  aria-label={t('docSidebar.moreActions')}
                  aria-haspopup="menu"
                  onPointerDown={stop}
                  onClick={(e) => openRowMenu(e, node, e.currentTarget)}
                >
                  <MoreGlyph size={ROW_ACTION_ICON_SIZE} />
                </button>
              </Tooltip>
            </span>
          </>
        )}
      </div>
      {isOpen && (
        <div role="group">
          {children.map((child) => (
            <TreeRow key={child.id} node={child} depth={depth + 1} parentType={node.type} ctx={ctx} />
          ))}
        </div>
      )}
    </>
  );
}
